import React, { useEffect, useMemo, useRef, useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  getOrders, createOrder, updateOrder, updateOrderStatus, deleteOrder, getClients, getStockAvailability, getCompanySettings,
  getFulfillmentSources, createDeliveryNote, fmt,
} from "@/lib/api";
import { Plus, ShoppingCart, Truck, Eye, X, Check, Trash2, Pencil, AlertTriangle, PackageCheck } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { BulkDeliverDialog } from "@/pages/orders/BulkDeliverDialog";
import { useT } from "@/i18n/I18nContext";
import { formatDate, isAllowedWeekday, nextAllowedDate, weekdayNames } from "@/lib/dates";
import { usePagination } from "@/hooks/usePagination";
import { TablePagination } from "@/components/TablePagination";

const statusBadge = {
  placed: "badge-warn", confirmed: "badge-ok",
  delivered: "bg-[hsl(var(--brand))] text-white", cancelled: "badge-bad",
};

export default function Orders() {
  const { can } = useAuth();
  const { t, td } = useT();
  const canDelete = can("admin", "manager");
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);
  const [avail, setAvail] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [view, setView] = useState(null);
  const [editOrder, setEditOrder] = useState(null);
  const [deliver, setDeliver] = useState(null);
  const [delOrder, setDelOrder] = useState(null);
  const [deliveryDays, setDeliveryDays] = useState([0, 1, 2, 3, 4]);
  const [confirmStock, setConfirmStock] = useState(null); // { id, shortages, products } when confirm is soft-blocked
  const [filterDate, setFilterDate] = useState("");
  const [filterMode, setFilterMode] = useState("before"); // "before" = due on/before, "exact" = that day only
  const [filterClient, setFilterClient] = useState("all");
  const [bulkSel, setBulkSel] = useState(() => new Set()); // open orders ticked for bulk delivery
  const [bulkOrders, setBulkOrders] = useState(null);      // snapshot passed to the bulk dialog

  const isOpenOrder = (o) => o.status === "placed" || o.status === "confirmed";
  const toggleBulk = (id) => setBulkSel((x) => { const n = new Set(x); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const filteredOrders = useMemo(() => {
    return orders.filter((o) => {
      if (filterClient !== "all" && o.client_id !== filterClient) return false;
      if (filterDate) {
        const d = (o.requested_date || "").slice(0, 10);
        if (!d) return false;
        if (filterMode === "exact" ? d !== filterDate : d > filterDate) return false;
      }
      return true;
    });
  }, [orders, filterDate, filterMode, filterClient]);
  const pager = usePagination(filteredOrders, 50);
  const pageOpenIds = pager.pageItems.filter(isOpenOrder).map((o) => o.id);
  const allPageSelected = pageOpenIds.length > 0 && pageOpenIds.every((id) => bulkSel.has(id));
  const togglePage = () => setBulkSel((x) => {
    const n = new Set(x);
    if (allPageSelected) pageOpenIds.forEach((id) => n.delete(id)); else pageOpenIds.forEach((id) => n.add(id));
    return n;
  });
  const bulkSelectedOrders = orders.filter((o) => bulkSel.has(o.id) && isOpenOrder(o));
  const bulkClientCount = new Set(bulkSelectedOrders.map((o) => o.client_id)).size;

  const load = async () => {
    const [o, c, a, cs] = await Promise.all([getOrders(), getClients(), getStockAvailability(), getCompanySettings().catch(() => null)]);
    setOrders(o); setClients(c); setAvail(a);
    if (cs?.delivery_days?.length) setDeliveryDays(cs.delivery_days);
    else if (Array.isArray(cs?.delivery_days)) setDeliveryDays(cs.delivery_days);
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status, override = false) => {
    try {
      await updateOrderStatus(id, status, override ? { override_stock: true } : {});
      toast.success(t("orders.statusToast", { status: t(`status.${status}`) }));
      setConfirmStock(null);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d && typeof d === "object" && d.code === "insufficient_stock_confirm") {
        setConfirmStock({ id, shortages: d.shortages || [], products: d.products || [] });
      } else {
        toast.error((typeof d === "string" ? d : null) || t("common.failed"));
      }
    }
  };

  const removeOrder = async () => {
    if (!delOrder) return;
    try {
      await deleteOrder(delOrder.id);
      toast.success(t("orders.orderDeleted", { code: delOrder.order_code }));
      setDelOrder(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("orders.failedDelete")); }
  };

  return (
    <Layout actions={
      <>
        {bulkSelectedOrders.length > 0 && (
          <Button size="sm" variant="outline" onClick={() => setBulkOrders(bulkSelectedOrders)} data-testid="bulk-deliver-button">
            <PackageCheck className="mr-1.5 h-4 w-4" /> {t("orders.bulkDeliverBtn", { orders: bulkSelectedOrders.length, clients: bulkClientCount })}
          </Button>
        )}
        <Button size="sm" onClick={() => setCreateOpen(true)} data-testid="add-order-button">
          <Plus className="mr-1.5 h-4 w-4" /> {t("orders.newOrder")}
        </Button>
      </>
    }>
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="flex items-center gap-2 text-base">
              <ShoppingCart className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("orders.title")} ({filteredOrders.length})
            </CardTitle>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={filterClient} onValueChange={setFilterClient}>
                <SelectTrigger className="h-9 w-[200px]" data-testid="orders-client-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("common.allClients")}</SelectItem>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterMode} onValueChange={setFilterMode}>
                <SelectTrigger className="h-9 w-[190px]" data-testid="orders-delivery-filter-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="before">{t("orders.dueBy")}</SelectItem>
                  <SelectItem value="exact">{t("orders.dueOn")}</SelectItem>
                </SelectContent>
              </Select>
              <Input type="date" className="h-9 w-[160px]" value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)} data-testid="orders-delivery-filter-date" />
              {filterDate && (
                <Button variant="ghost" size="sm" onClick={() => setFilterDate("")} data-testid="orders-delivery-filter-clear">
                  <X className="mr-1 h-3.5 w-3.5" /> {t("common.clear")}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {orders.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-center">
              <ShoppingCart className="h-10 w-10 text-muted-foreground/50" />
              <p className="mt-3 text-sm text-muted-foreground">{t("orders.empty")}</p>
            </div>
          ) : filteredOrders.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-center" data-testid="orders-no-match">
              <ShoppingCart className="h-10 w-10 text-muted-foreground/50" />
              <p className="mt-3 text-sm text-muted-foreground">{t("common.noMatchClient")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto thin-scroll">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[36px]">
                      <Checkbox checked={allPageSelected} onCheckedChange={togglePage} disabled={pageOpenIds.length === 0} aria-label={t("orders.bulkSelectAll")} data-testid="orders-select-all" />
                    </TableHead>
                    <TableHead>{t("orders.order")}</TableHead>
                    <TableHead>{t("common.client")}</TableHead>
                    <TableHead>{t("orders.items")}</TableHead>
                    <TableHead className="text-right">{t("common.total")}</TableHead>
                    <TableHead>{t("common.status")}</TableHead>
                    <TableHead>{t("orders.deliveryDate")}</TableHead>
                    <TableHead>{t("common.date")}</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pager.pageItems.map((o) => (
                    <TableRow key={o.id} data-testid={`order-row-${o.id}`} className={bulkSel.has(o.id) ? "bg-[hsl(var(--brand)/0.05)]" : ""}>
                      <TableCell>
                        {isOpenOrder(o) && (
                          <Checkbox checked={bulkSel.has(o.id)} onCheckedChange={() => toggleBulk(o.id)} aria-label={t("orders.bulkSelectOrder")} data-testid={`order-select-${o.id}`} />
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{o.order_code}</TableCell>
                      <TableCell className="font-medium">{o.client_name}
                        <div className="text-xs text-muted-foreground">{t("clients.level", { n: o.price_level })}</div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{o.items?.length || 0}</TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{fmt(o.total, 2)} {o.currency}</TableCell>
                      <TableCell>
                        <div className="flex flex-col items-start gap-1">
                          <Badge className={statusBadge[o.status]}>{t(`status.${o.status}`)}</Badge>
                          {o.has_stock_shortage && (
                            <Badge variant="outline" className="border-[hsl(var(--warning))] text-[hsl(var(--warning))] gap-1" title={(o.short_items || []).join(", ")} data-testid={`order-lowstock-${o.id}`}>
                              <AlertTriangle className="h-3 w-3" /> {t("orders.lowStockBadge")}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-xs font-medium">{formatDate(o.requested_date, "—")}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{formatDate(o.created_at)}</TableCell>
                      <TableCell className="text-right whitespace-nowrap">
                        <Button variant="ghost" size="icon" title={t("common.view")} onClick={() => setView(o)} data-testid={`order-view-${o.id}`}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        {o.status !== "delivered" && (
                          <Button variant="ghost" size="icon" title={t("orders.editBtn")} onClick={() => setEditOrder(o)} data-testid={`order-edit-${o.id}`}>
                            <Pencil className="h-4 w-4 text-[hsl(var(--brand))]" />
                          </Button>
                        )}
                        {(o.status === "placed" || o.status === "confirmed") && (
                          <Button variant="ghost" size="icon" title={t("orders.createDelivery")} onClick={() => setDeliver(o)} data-testid={`order-deliver-${o.id}`}>
                            <Truck className="h-4 w-4 text-[hsl(var(--brand))]" />
                          </Button>
                        )}
                        {o.status === "placed" && (
                          <Button variant="ghost" size="icon" title={t("orders.confirm")} onClick={() => setStatus(o.id, "confirmed")} data-testid={`order-confirm-${o.id}`}>
                            <Check className="h-4 w-4 text-[hsl(var(--success))]" />
                          </Button>
                        )}
                        {(o.status === "placed" || o.status === "confirmed") && (
                          <Button variant="ghost" size="icon" title={t("orders.cancel")} onClick={() => setStatus(o.id, "cancelled")} data-testid={`order-cancel-${o.id}`}>
                            <X className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        )}
                        {canDelete && o.status !== "delivered" && (
                          <Button variant="ghost" size="icon" title={t("orders.deleteBtn")} onClick={() => setDelOrder(o)} data-testid={`order-delete-${o.id}`}>
                            <Trash2 className="h-4 w-4 text-[hsl(var(--danger))]" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                page={pager.page} pageCount={pager.pageCount} pageSize={pager.pageSize}
                total={pager.total} start={pager.start} end={pager.end}
                onPageChange={pager.setPage} onPageSizeChange={pager.setPageSize}
                testid="orders-pagination"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {createOpen && (
        <CreateOrderDialog clients={clients} avail={avail} deliveryDays={deliveryDays}
          onClose={() => setCreateOpen(false)}
          onCreated={() => { setCreateOpen(false); load(); }} />
      )}
      {editOrder && (
        <CreateOrderDialog clients={clients} avail={avail} deliveryDays={deliveryDays} order={editOrder}
          onClose={() => setEditOrder(null)}
          onCreated={() => { setEditOrder(null); load(); }} />
      )}
      <ViewOrderDialog order={view} onClose={() => setView(null)} />
      {deliver && (
        <DeliverDialog order={deliver} allOrders={orders} onClose={() => setDeliver(null)}
          onDelivered={() => { setDeliver(null); load(); }} />
      )}
      {bulkOrders && (
        <BulkDeliverDialog
          orders={bulkOrders}
          onClose={() => setBulkOrders(null)}
          onDelivered={(_n, keepOpen) => { load(); if (!keepOpen) { setBulkOrders(null); setBulkSel(new Set()); } }}
        />
      )}

      <AlertDialog open={!!delOrder} onOpenChange={(o) => !o && setDelOrder(null)}>
        <AlertDialogContent data-testid="order-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("orders.deleteOrder")}</AlertDialogTitle>
            <AlertDialogDescription>
              {delOrder ? t("orders.deleteOrderDesc", { code: delOrder.order_code, client: delOrder.client_name }) : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="order-delete-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={removeOrder} className="bg-[hsl(var(--danger))] text-white hover:bg-[hsl(var(--danger))]/90" data-testid="order-delete-confirm">{t("orders.deleteBtn")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmStock} onOpenChange={(o) => !o && setConfirmStock(null)}>
        <AlertDialogContent data-testid="order-confirm-stock-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-[hsl(var(--warning))]" /> {t("orders.insufficientStockTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>{t("orders.insufficientStockBody")}</AlertDialogDescription>
          </AlertDialogHeader>
          {confirmStock?.shortages?.length > 0 && (
            <ul className="space-y-1 rounded-md bg-muted/50 p-3 text-sm">
              {confirmStock.shortages.map((s, i) => (
                <li key={i} className="flex justify-between gap-2" data-testid={`shortage-line-${i}`}>
                  <span className="font-medium">{s.product_type} ({s.unit})</span>
                  <span className="text-muted-foreground">{t("orders.insufficientStockLine", { requested: s.requested, available: s.available })}</span>
                </li>
              ))}
            </ul>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="order-confirm-stock-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => confirmStock && setStatus(confirmStock.id, "confirmed", true)} data-testid="order-confirm-stock-anyway">{t("orders.confirmAnyway")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}

function CreateOrderDialog({ clients, avail, deliveryDays, order, onClose, onCreated }) {
  const { t, td } = useT();
  const editing = !!order;
  const today = new Date().toISOString().slice(0, 10);
  const initialClient = clients.find((c) => c.id === (order?.client_id || ""));
  const initialAddrId = order
    ? (initialClient?.addresses?.find((a) => a.address === order.delivery_address)?.id || "")
    : "";
  const [clientId, setClientId] = useState(order?.client_id || "");
  const [orderDate, setOrderDate] = useState(order?.order_date || today);
  const [deliveryDate, setDeliveryDate] = useState(() =>
    order?.requested_date ? String(order.requested_date).slice(0, 10) : nextAllowedDate(deliveryDays, today));
  const [dateAvail, setDateAvail] = useState(avail);
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [items, setItems] = useState(() =>
    (order?.items || []).map((it) => ({
      _rid: (crypto?.randomUUID?.() || `r${Math.random()}`),
      product: it.product_type, unit: it.unit, quantity: String(it.quantity),
    })));
  const [notes, setNotes] = useState(order?.notes || "");
  const [addressId, setAddressId] = useState(initialAddrId);
  const [customOrderNo, setCustomOrderNo] = useState(order?.custom_order_no || "");
  const [invoiceNo, setInvoiceNo] = useState(order?.invoice_no || "");
  const [saving, setSaving] = useState(false);
  const [confirmSL, setConfirmSL] = useState(null); // { message } when min-shelf-life confirmation is needed
  // Preserve the prefilled address/refs on the first render when editing (skip the client-change reset once).
  const skipClientReset = useRef(editing ? order.client_id : null);
  const client = clients.find((c) => c.id === clientId);
  const level = client?.price_level || 1;
  const clientAddresses = client?.addresses || [];
  const deliveryAddress = clientAddresses.find((a) => a.id === addressId)?.address
    || (editing ? order.delivery_address : "") || client?.address || "";

  // When the client changes, default the delivery address to their default (or first) saved address.
  useEffect(() => {
    if (skipClientReset.current === clientId) { skipClientReset.current = null; return; }
    const def = clientAddresses.find((a) => a.is_default) || clientAddresses[0];
    setAddressId(def?.id || "");
    setCustomOrderNo("");
    setInvoiceNo("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  // Point-in-time stock: refetch availability for the selected (possibly backdated) order date.
  useEffect(() => {
    let active = true;
    setLoadingAvail(true);
    getStockAvailability(orderDate)
      .then((a) => { if (active) setDateAvail(a); })
      .catch(() => { if (active) setDateAvail([]); })
      .finally(() => { if (active) setLoadingAvail(false); });
    return () => { active = false; };
  }, [orderDate]);

  // Group availability by product -> each product offers its package types (units) in a dropdown.
  const grouped = useMemo(() => {
    const m = {};
    for (const r of dateAvail) (m[r.product_type] ||= []).push(r);
    return Object.entries(m)
      .map(([product, units]) => ({ product, units }))
      .sort((a, b) => a.product.localeCompare(b.product));
  }, [dateAvail]);

  const priceFor = (row) => Number(row?.prices?.[`level_${level}`] || 0);
  const unitsFor = (product) => grouped.find((g) => g.product === product)?.units || [];
  const rowFor = (product, unit) => dateAvail.find((r) => r.product_type === product && r.unit === unit);

  const addItem = () => {
    const first = grouped[0];
    setItems((x) => [...x, {
      _rid: (crypto?.randomUUID?.() || `r${Date.now()}${x.length}`),
      product: first?.product || "",
      unit: first?.units?.[0]?.unit || "",
      quantity: "",
    }]);
  };
  const setItem = (i, patch) => setItems((x) => x.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));
  const rmItem = (i) => setItems((x) => x.filter((_, idx) => idx !== i));

  const total = useMemo(() => items.reduce((s, it) => {
    const r = rowFor(it.product, it.unit); return s + (Number(it.quantity) || 0) * priceFor(r);
  }, 0),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [items, level, dateAvail]);

  const submit = async (override = false) => {
    if (!clientId) { toast.error(t("orders.selectClient")); return; }
    const payloadItems = items
      .filter((it) => it.product && it.unit && Number(it.quantity) > 0)
      .map((it) => ({ product_type: it.product, unit: it.unit, quantity: Number(it.quantity) }));
    if (payloadItems.length === 0) { toast.error(t("orders.addOneItem")); return; }
    if (!deliveryDate) { toast.error(t("orders.deliveryDateRequired")); return; }
    if (!isAllowedWeekday(deliveryDate, deliveryDays)) {
      toast.error(t("orders.deliveryDayNotAllowed", { days: weekdayNames(deliveryDays) }));
      return;
    }
    setSaving(true);
    try {
      const body = { client_id: clientId, items: payloadItems, notes, order_date: orderDate, requested_date: deliveryDate, delivery_address: deliveryAddress, custom_order_no: customOrderNo, invoice_no: invoiceNo, override_min_shelf_life: override };
      let res;
      if (editing) {
        res = await updateOrder(order.id, body);
        toast.success(t("orders.orderUpdatedToast"));
      } else {
        res = await createOrder(body);
        toast.success(t("orders.orderPlacedToast"));
      }
      if (res?.has_stock_shortage) {
        const names = (res.stock_shortages || []).map((s) => s.product_type);
        toast.warning(t("orders.lowStockWarning", { products: [...new Set(names)].join(", ") }), { duration: 6000 });
      }
      onCreated();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d && typeof d === "object" && d.code === "min_shelf_life_confirm") {
        setConfirmSL({ message: d.message || t("orders.shortShelfLifeConfirm") });
      } else {
        toast.error((typeof d === "string" ? d : null) || t("orders.failedPlace"));
      }
    } finally { setSaving(false); }
  };

  const isBackdated = orderDate !== today;

  return (
    <>
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>{editing ? t("orders.editOrder") : t("orders.newOrder")}</DialogTitle></DialogHeader>
        <div className="space-y-4 max-h-[65vh] overflow-y-auto thin-scroll pr-1">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>{t("common.client")}</Label>
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger data-testid="order-client-select"><SelectValue placeholder={t("orders.selectClient")} /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{t("orders.clientOption", { name: c.name, level: c.price_level })}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t("orders.orderDate")}</Label>
              <Input
                type="date"
                max={today}
                value={orderDate}
                onChange={(e) => setOrderDate(e.target.value || today)}
                data-testid="order-date-input"
              />
              <p className="text-[11px] text-muted-foreground">
                {isBackdated ? t("orders.backdateHint") : t("orders.orderDateHint")}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label>{t("orders.deliveryDate")} <span className="text-[hsl(var(--danger))]">*</span></Label>
              <Input
                type="date"
                min={today}
                value={deliveryDate}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v && !isAllowedWeekday(v, deliveryDays)) {
                    toast.error(t("orders.deliveryDayNotAllowed", { days: weekdayNames(deliveryDays) }));
                    return;
                  }
                  setDeliveryDate(v);
                }}
                data-testid="order-delivery-date-input"
              />
              <p className="text-[11px] text-muted-foreground">{t("orders.deliveryDaysHint", { days: weekdayNames(deliveryDays) })}</p>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>{t("orders.deliveryAddress")}</Label>
              {clientId && clientAddresses.length > 0 ? (
                <Select value={addressId} onValueChange={setAddressId}>
                  <SelectTrigger data-testid="order-address-select"><SelectValue placeholder={t("orders.selectAddress")} /></SelectTrigger>
                  <SelectContent>
                    {clientAddresses.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.label ? `${a.label} — ${a.address}` : a.address}{a.is_default ? ` (${t("clients.defaultAddr")})` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-[11px] text-muted-foreground" data-testid="order-address-empty">
                  {clientId ? t("orders.noClientAddress") : t("orders.selectClientFirst")}
                </p>
              )}
            </div>
            {clientId && client?.requires_custom_order_no && (
              <div className="space-y-1.5">
                <Label>{t("orders.customOrderNo")}</Label>
                <Input value={customOrderNo} onChange={(e) => setCustomOrderNo(e.target.value)} placeholder={t("orders.customOrderNoPh")} data-testid="order-custom-no-input" />
              </div>
            )}
            {clientId && client?.requires_invoice_no && (
              <div className="space-y-1.5">
                <Label>{t("orders.invoiceNo")}</Label>
                <Input value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} placeholder={t("orders.invoiceNoPh")} data-testid="order-invoice-no-input" />
              </div>
            )}
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <Label>{t("orders.items")}</Label>
              <Button variant="outline" size="sm" onClick={addItem} disabled={grouped.length === 0} data-testid="order-add-item-button">
                <Plus className="mr-1 h-3.5 w-3.5" /> {t("orders.addItem")}
              </Button>
            </div>
            {loadingAvail && <p className="text-sm text-muted-foreground" data-testid="order-avail-loading">{t("orders.loadingStock")}</p>}
            {!loadingAvail && grouped.length === 0 && <p className="text-sm text-muted-foreground">{isBackdated ? t("orders.noSellableDate") : t("orders.noSellable")}</p>}
            <div className="space-y-3">
              {items.map((it, i) => {
                const r = rowFor(it.product, it.unit);
                const units = unitsFor(it.product);
                const lineTotal = (Number(it.quantity) || 0) * priceFor(r);
                const over = r && Number(it.quantity) > r.available + 1e-6;
                return (
                  <div key={it._rid} className="rounded-lg border border-border p-2.5" data-testid={`order-item-${i}`}>
                    <div className="grid grid-cols-12 items-end gap-2">
                      <div className="col-span-4 space-y-1">
                        <Label className="text-[11px] text-muted-foreground">{t("common.product")}</Label>
                        <Select value={it.product} onValueChange={(v) => setItem(i, { product: v, unit: unitsFor(v)[0]?.unit || "" })}>
                          <SelectTrigger data-testid={`order-item-product-${i}`}><SelectValue placeholder={t("orders.selectProduct")} /></SelectTrigger>
                          <SelectContent>
                            {grouped.map((g) => (
                              <SelectItem key={g.product} value={g.product}>{td(g.product)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-3 space-y-1">
                        <Label className="text-[11px] text-muted-foreground">{t("common.packaging")}</Label>
                        <Select value={it.unit} onValueChange={(v) => setItem(i, { unit: v })} disabled={!it.product}>
                          <SelectTrigger data-testid={`order-item-unit-${i}`}><SelectValue placeholder={t("orders.selectPackaging")} /></SelectTrigger>
                          <SelectContent>
                            {units.map((u) => (
                              <SelectItem key={u.unit} value={u.unit} data-testid={`order-item-unit-option-${i}-${u.unit}`}>{u.unit}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2 space-y-1">
                        <Label className="text-[11px] text-muted-foreground">{t("common.qty")}</Label>
                        <Input type="number" className={`text-right font-mono ${over ? "border-[hsl(var(--danger))]" : ""}`} placeholder={t("common.qty")} value={it.quantity} onChange={(e) => setItem(i, { quantity: e.target.value })} data-testid={`order-item-qty-${i}`} />
                      </div>
                      <div className="col-span-2 pb-2 text-right font-mono text-sm tabular-nums">{fmt(lineTotal, 2)}</div>
                      <div className="col-span-1">
                        <Button variant="ghost" size="icon" onClick={() => rmItem(i)} data-testid={`order-item-remove-${i}`}><Trash2 className="h-4 w-4 text-muted-foreground" /></Button>
                      </div>
                    </div>
                    <div className="mt-1.5 text-[11px] text-muted-foreground">{t("orders.availLine", { qty: fmt(r?.available, 2), unit: r?.unit || "", price: fmt(priceFor(r), 2) })}</div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>{t("common.notes")}</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="order-notes-input" />
          </div>
        </div>
        <DialogFooter className="items-center justify-between sm:justify-between">
          <div className="font-mono text-sm">{t("common.total")}: <span className="font-semibold tabular-nums">{fmt(total, 2)}</span></div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
            <Button onClick={() => submit()} disabled={saving} data-testid="order-submit-button">{editing ? t("common.save") : t("orders.placeOrder")}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <AlertDialog open={!!confirmSL} onOpenChange={(o) => !o && setConfirmSL(null)}>
      <AlertDialogContent data-testid="order-shelf-life-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle>{t("orders.shortShelfLifeTitle")}</AlertDialogTitle>
          <AlertDialogDescription>{confirmSL?.message}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="order-shelf-life-cancel">{t("common.cancel")}</AlertDialogCancel>
          <AlertDialogAction onClick={() => { setConfirmSL(null); submit(true); }} data-testid="order-shelf-life-confirm-btn">
            {t("orders.sellAnyway")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}

function ViewOrderDialog({ order, onClose }) {
  const { t, td } = useT();
  if (!order) return null;
  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-mono text-base">{order.order_code}</DialogTitle>
          <DialogDescription>{order.client_name} · {t("clients.level", { n: order.price_level })} · {t(`status.${order.status}`)}</DialogDescription>
        </DialogHeader>
        <Table>
          <TableHeader><TableRow><TableHead>{t("common.product")}</TableHead><TableHead className="text-right">{t("common.qty")}</TableHead><TableHead className="text-right">{t("orders.unitPrice")}</TableHead><TableHead className="text-right">{t("common.total")}</TableHead></TableRow></TableHeader>
          <TableBody>
            {order.items?.map((it, i) => (
              <TableRow key={i}>
                <TableCell>{td(it.product_type)} <span className="text-muted-foreground">({it.unit})</span>
                  {it.delivery_expiry_date && (
                    <div className="text-[11px] text-muted-foreground" data-testid={`order-item-delivery-expiry-${i}`}>
                      {t("orders.deliveryExpiry")}: {formatDate(it.delivery_expiry_date)}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-right font-mono tabular-nums">{fmt(it.quantity, 2)}</TableCell>
                <TableCell className="text-right font-mono tabular-nums">{fmt(it.unit_price, 2)}</TableCell>
                <TableCell className="text-right font-mono tabular-nums">{fmt(it.line_total, 2)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="text-right font-mono text-sm">{t("common.total")}: <span className="font-semibold">{fmt(order.total, 2)} {order.currency}</span></div>
        {order.notes && <p className="text-sm text-muted-foreground">{order.notes}</p>}
      </DialogContent>
    </Dialog>
  );
}

function DeliverDialog({ order, allOrders, onClose, onDelivered }) {
  const { t, td } = useT();
  const siblings = allOrders.filter(
    (o) => o.client_id === order.client_id && (o.status === "placed" || o.status === "confirmed")
  );
  const [selectedIds, setSelectedIds] = useState([order.id]);
  // lines: [{key, product_type, unit, ordered, allocations: [{source_id, quantity}]}]
  const [lines, setLines] = useState([]);
  const [sources, setSources] = useState({}); // key -> [sourceObj]
  const [saving, setSaving] = useState(false);
  const [confirmSL, setConfirmSL] = useState(null); // min-shelf-life confirmation

  const toggleOrder = (id) =>
    setSelectedIds((x) => (x.includes(id) ? x.filter((i) => i !== id) : [...x, id]));

  // Recompute aggregated lines + fetch fulfillment sources whenever selection changes
  useEffect(() => {
    const selected = siblings.filter((o) => selectedIds.includes(o.id));
    const agg = {}; // key -> {product_type, unit, ordered}
    selected.forEach((o) => (o.items || []).forEach((it) => {
      const key = `${it.product_type}|${it.unit}`;
      agg[key] = agg[key] || { product_type: it.product_type, unit: it.unit, ordered: 0 };
      agg[key].ordered += Number(it.quantity);
    }));
    const keys = Object.keys(agg);
    Promise.all(keys.map((k) => getFulfillmentSources(agg[k].product_type, agg[k].unit)))
      .then((results) => {
        const srcMap = {};
        keys.forEach((k, i) => { srcMap[k] = results[i]; });
        setSources(srcMap);
        setLines(keys.map((k) => ({
          ...agg[k], key: k,
          allocations: [{ _rid: (crypto?.randomUUID?.() || `a${Date.now()}`), source_id: (srcMap[k][0] || {}).id || "", quantity: String(agg[k].ordered) }],
        })));
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIds.join(",")]);

  const setAlloc = (key, idx, patch) => setLines((x) => x.map((ln) =>
    ln.key === key
      ? { ...ln, allocations: ln.allocations.map((a, i) => (i === idx ? { ...a, ...patch } : a)) }
      : ln));
  const addAlloc = (key) => setLines((x) => x.map((ln) =>
    ln.key === key ? { ...ln, allocations: [...ln.allocations, { _rid: (crypto?.randomUUID?.() || `a${Date.now()}${ln.allocations.length}`), source_id: "", quantity: "" }] } : ln));
  const rmAlloc = (key, idx) => setLines((x) => x.map((ln) =>
    ln.key === key ? { ...ln, allocations: ln.allocations.filter((_, i) => i !== idx) } : ln));

  const allocatedFor = (ln) => ln.allocations.reduce((s, a) => s + (Number(a.quantity) || 0), 0);

  const submit = async (override = false) => {
    const payloadLines = [];
    for (const ln of lines) {
      for (const a of ln.allocations) {
        if (!a.source_id || Number(a.quantity) <= 0) continue;
        const src = (sources[ln.key] || []).find((s) => s.id === a.source_id);
        if (!src) continue;
        payloadLines.push({
          product_type: ln.product_type, unit: ln.unit, quantity: Number(a.quantity),
          source_type: src.source_type, source_id: a.source_id,
        });
      }
    }
    if (payloadLines.length === 0) { toast.error(t("orders.selectSourceQty")); return; }
    setSaving(true);
    try {
      await createDeliveryNote({ order_ids: selectedIds, lines: payloadLines, override_min_shelf_life: override });
      toast.success(t("orders.dnCreated"));
      onDelivered();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d && typeof d === "object" && d.code === "min_shelf_life_confirm") {
        setConfirmSL({ message: d.message || t("orders.shortShelfLifeConfirm") });
      } else {
        toast.error((typeof d === "string" ? d : null) || t("orders.failedDn"));
      }
    } finally { setSaving(false); }
  };

  return (
    <>
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("orders.createDelivery")}</DialogTitle>
          <DialogDescription>{order.client_name} · {t("orders.deliverDesc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[62vh] overflow-y-auto thin-scroll pr-1">
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">{t("orders.ordersToInclude")}</Label>
            <div className="mt-1.5 space-y-1.5">
              {siblings.map((o) => (
                <label key={o.id} className="flex cursor-pointer items-center justify-between rounded-lg border border-border px-3 py-2" data-testid={`deliver-order-${o.id}`}>
                  <span className="flex items-center gap-2">
                    <input type="checkbox" checked={selectedIds.includes(o.id)} onChange={() => toggleOrder(o.id)} className="h-4 w-4 accent-[hsl(var(--brand))]" />
                    <span className="font-mono text-xs">{o.order_code}</span>
                    <Badge className="badge-warn">{t(`status.${o.status}`)}</Badge>
                  </span>
                  <span className="text-xs text-muted-foreground">{(o.items || []).map((i) => `${td(i.product_type)} ${fmt(i.quantity, 2)}${i.unit}`).join(", ")}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">{t("orders.allocateFrom")}</Label>
            <div className="mt-1.5 space-y-3">
              {lines.length === 0 && <p className="text-sm text-muted-foreground">{t("orders.selectOneOrder")}</p>}
              {lines.map((ln) => {
                const opts = sources[ln.key] || [];
                const allocated = allocatedFor(ln);
                const over = allocated > ln.ordered + 1e-6;
                const short = allocated < ln.ordered - 1e-6;
                return (
                  <div key={ln.key} className="rounded-lg border border-border p-3" data-testid={`deliver-line-${ln.key}`}>
                    <div className="mb-2 flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium">{td(ln.product_type)} <span className="text-muted-foreground">({ln.unit})</span></span>
                        <div className="text-[11px] text-muted-foreground">{t("orders.ordered", { n: fmt(ln.ordered, 2) })}</div>
                      </div>
                      <Badge className={over ? "badge-bad" : short ? "badge-warn" : "badge-ok"} data-testid={`deliver-alloc-status-${ln.key}`}>
                        {t("orders.allocated", { a: fmt(allocated, 2), o: fmt(ln.ordered, 2) })}
                      </Badge>
                    </div>
                    <div className="space-y-2">
                      {ln.allocations.map((a, idx) => (
                        <div key={a._rid || `${ln.key}-${idx}`} className="grid grid-cols-12 items-center gap-2" data-testid={`deliver-alloc-${ln.key}-${idx}`}>
                          <div className="col-span-7">
                            <Select value={a.source_id} onValueChange={(v) => setAlloc(ln.key, idx, { source_id: v })}>
                              <SelectTrigger data-testid={`deliver-source-${ln.key}-${idx}`}><SelectValue placeholder={opts.length ? t("orders.selectSource") : t("orders.noStock")} /></SelectTrigger>
                              <SelectContent>
                                {opts.map((s) => (
                                  <SelectItem key={s.id} value={s.id}>
                                    {s.code} · {t("orders.sourceLeft", { n: fmt(s.remaining, 2) })}{s.expiry_date ? ` · ${t("orders.expShort")} ${formatDate(s.expiry_date)}` : ""}{s.source_type === "production" ? ` ${t("orders.batchTag")}` : ""}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="col-span-4">
                            <Input type="number" className="text-right font-mono" placeholder={t("common.qty")} value={a.quantity} onChange={(e) => setAlloc(ln.key, idx, { quantity: e.target.value })} data-testid={`deliver-qty-${ln.key}-${idx}`} />
                          </div>
                          <div className="col-span-1">
                            {ln.allocations.length > 1 && (
                              <Button variant="ghost" size="icon" onClick={() => rmAlloc(ln.key, idx)} data-testid={`deliver-alloc-remove-${ln.key}-${idx}`}>
                                <Trash2 className="h-4 w-4 text-muted-foreground" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <Button variant="ghost" size="sm" className="mt-1.5 h-7 px-2 text-xs" onClick={() => addAlloc(ln.key)} disabled={opts.length <= 1} data-testid={`deliver-add-lot-${ln.key}`}>
                      <Plus className="mr-1 h-3.5 w-3.5" /> {t("orders.addAnotherLot")}
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={() => submit()} disabled={saving} data-testid="deliver-submit-button">{t("orders.createDeduct")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <AlertDialog open={!!confirmSL} onOpenChange={(o) => !o && setConfirmSL(null)}>
      <AlertDialogContent data-testid="deliver-shelf-life-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle>{t("orders.shortShelfLifeTitle")}</AlertDialogTitle>
          <AlertDialogDescription>{confirmSL?.message}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="deliver-shelf-life-cancel">{t("common.cancel")}</AlertDialogCancel>
          <AlertDialogAction onClick={() => { setConfirmSL(null); submit(true); }} data-testid="deliver-shelf-life-confirm-btn">
            {t("orders.sellAnyway")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
